import { FC, useEffect, useRef, useState } from 'react'
import { SectionSingleMediaWithDescriptionProps } from '@portfolio/models'
import { styled } from '@portfolio/styles'
import {
  Media,
  MediaWithBackground,
  SectionWrapper,
} from '@portfolio/components'

const PLAYBACK_VISIBLE_RATIO = 0.5
const RESET_PLAYBACK_HIDDEN_MS = 2000

const StyledSectionWrapper = styled(SectionWrapper, {
  display: 'grid',
  rowGap: '$20',
})

const Description = styled('p', {
  projectFont: 'body04',
  color: '$black_06',
  maxWidth: '$singleMediaMaxWidth',
  marginLeft: 'auto',
  marginRight: 'auto',
})
const MediaWrap = styled('div', {
  variants: {
    mode: {
      'full-width': {
        '@lg': {
          containerSpace: 'medium',
        },
      },
      'with-margins': {
        containerSpace: 'medium',
      },
      inline: {},
    },
  },
})
const DescriptionWrap = styled('div', {
  variants: {
    mode: {
      'full-width': {
        containerSpace: 'medium',
      },
      'with-margins': {
        containerSpace: 'medium',
      },
      inline: {},
    },
  },
})
export const SectionSingleMediaWithDescription: FC<
  SectionSingleMediaWithDescriptionProps
> = ({ mode = 'full-width', mediaField, mediaDescription, priority }) => {
  const media = mediaField?.media
  const mediaWrapRef = useRef<HTMLDivElement>(null)
  const videoElementRef = useRef<HTMLVideoElement | null>(null)
  const resetTimeoutRef = useRef<number | null>(null)
  const shouldResetOnNextVisibleRef = useRef(false)
  const pausedByVisibilityRef = useRef(false)
  const [mediaResetVersion, setMediaResetVersion] = useState(0)
  const isVideoMedia = media?.mediaPayload.type === 'mux-video'
  const mediaElementKey = isVideoMedia
    ? `article-video-${mediaResetVersion}`
    : 'article-media'

  useEffect(() => {
    if (!isVideoMedia || !mediaWrapRef.current) {
      return
    }

    const mediaWrapElement = mediaWrapRef.current

    const handleVideoEvent = (event: Event) => {
      if (!(event.target instanceof HTMLVideoElement)) {
        return
      }

      videoElementRef.current = event.target
    }

    mediaWrapElement.addEventListener('play', handleVideoEvent, true)
    mediaWrapElement.addEventListener('loadeddata', handleVideoEvent, true)

    const clearResetTimeout = () => {
      if (resetTimeoutRef.current === null) {
        return
      }

      window.clearTimeout(resetTimeoutRef.current)
      resetTimeoutRef.current = null
    }

    const observer = new IntersectionObserver(
      (entries: IntersectionObserverEntry[]) => {
        const entry = entries[0]
        const currentVideoElement = videoElementRef.current
        const isVisibleEnough =
          entry.intersectionRatio >= PLAYBACK_VISIBLE_RATIO

        if (!currentVideoElement) {
          return
        }

        if (!isVisibleEnough) {
          if (!currentVideoElement.paused) {
            currentVideoElement.pause()
            pausedByVisibilityRef.current = true
          }

          if (!pausedByVisibilityRef.current) {
            clearResetTimeout()
            return
          }

          if (entry.intersectionRatio === 0) {
            if (resetTimeoutRef.current === null) {
              resetTimeoutRef.current = window.setTimeout(() => {
                shouldResetOnNextVisibleRef.current = true
                resetTimeoutRef.current = null
              }, RESET_PLAYBACK_HIDDEN_MS)
            }
          } else {
            clearResetTimeout()
          }

          return
        }

        clearResetTimeout()

        if (shouldResetOnNextVisibleRef.current) {
          shouldResetOnNextVisibleRef.current = false
          pausedByVisibilityRef.current = false
          setMediaResetVersion((currentVersion) => currentVersion + 1)
          return
        }

        if (pausedByVisibilityRef.current) {
          pausedByVisibilityRef.current = false
          if (currentVideoElement.paused) {
            currentVideoElement.play().catch((): undefined => undefined)
          }
        }
      },
      {
        threshold: [0, PLAYBACK_VISIBLE_RATIO],
      },
    )

    observer.observe(mediaWrapElement)

    return () => {
      observer.disconnect()
      mediaWrapElement.removeEventListener('play', handleVideoEvent, true)
      mediaWrapElement.removeEventListener('loadeddata', handleVideoEvent, true)
      clearResetTimeout()
      videoElementRef.current = null
      shouldResetOnNextVisibleRef.current = false
      pausedByVisibilityRef.current = false
    }
  }, [isVideoMedia, mediaResetVersion])

  if (!media) {
    return null
  }

  return (
    <StyledSectionWrapper
      type={mode === 'full-width' ? 'RICH_TEXT_LG' : 'RICH_TEXT_SM'}
    >
      <MediaWrap mode={mode} ref={mediaWrapRef}>
        {mediaField._type === 'simpleMedia' ? (
          <Media key={mediaElementKey} {...media} priority={priority} />
        ) : (
          <MediaWithBackground
            key={mediaElementKey}
            media={media}
            color={mediaField.color}
            mediaBorder={mediaField.mediaBorder}
            priority={priority}
            appearance="singleMedia"
          />
        )}
      </MediaWrap>
      {mediaDescription && (
        <DescriptionWrap mode={mode}>
          <Description>↑ {mediaDescription}</Description>
        </DescriptionWrap>
      )}
    </StyledSectionWrapper>
  )
}
